import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';
import ArrowIcon from '../../../static/icons/collapsed.svg';
import { formatQuestionLabel } from '../../SubmissionView/submissionViewUtils';
import QuestionNavigationContent from '../QuestionNavigationContent';

const PANEL_WIDTH = 450;
const PURPLE = '#7200e0';

const QuestionNavigationPanel = ({
  questions = [],
  currentQuestionIndex = 0,
  onQuestionSelect,
  submission,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    let left = rect.left + rect.width / 2;
    const half = PANEL_WIDTH / 2;
    left = Math.min(
      Math.max(left, half + 12),
      window.innerWidth - half - 12,
    );
    setPosition({ top: rect.bottom + 8, left });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buttonRef.current?.contains(event.target)) return;

      if (
        isOpen &&
        panelRef.current &&
        !panelRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    if (!isOpen) return undefined;
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const currentSerial = questions[currentQuestionIndex]?.serialNumber;

  const handleQuestionSelect = (targetIndex) => {
    try {
      onQuestionSelect?.(targetIndex);
    } finally {
      setIsOpen(false);
    }
  };

  const renderPanel = () => {
    if (!isOpen) return null;

    const panel = (
      <Panel
        ref={panelRef}
        className="question-navigation-panel"
        role="dialog"
        aria-label="Questions"
        style={{ top: `${position.top}px`, left: `${position.left}px` }}
      >
        <QuestionNavigationContent
          questions={questions}
          currentQuestionIndex={currentQuestionIndex}
          submission={submission}
          onQuestionSelect={handleQuestionSelect}
          onClose={() => setIsOpen(false)}
        />
      </Panel>
    );

    return ReactDOM.createPortal(panel, document.body);
  };

  return (
    <Container>
      <DropdownButton
        ref={buttonRef}
        onClick={() => {
          setIsOpen((open) => {
            if (!open) updatePosition();
            return !open;
          });
        }}
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <DropdownLabel>
          {formatQuestionLabel(currentSerial)}
        </DropdownLabel>
        <DropdownIcon src={ArrowIcon} $isOpen={isOpen} alt="" />
      </DropdownButton>
      {renderPanel()}
    </Container>
  );
};

export default QuestionNavigationPanel;

const pillButtonReset = css`
  margin: 0;
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  line-height: 1.3;
  text-decoration: none;
  box-shadow: none;
`;

const Container = styled.div`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  z-index: 2;
`;

const DropdownButton = styled.button`
  ${pillButtonReset}
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 16px;
  background: #ffffff;
  border: 1.5px solid #4b464f;
  border-radius: 20px;
  font-weight: 600;
  font-size: var(--font-size-s, 14px);
  color: #4b464f;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease;
  min-width: 140px;
  justify-content: space-between;
  height: 32px;

  &:hover {
    border-color: ${PURPLE};
    color: ${PURPLE};
  }
`;

const DropdownLabel = styled.span`
  white-space: nowrap;
`;

const DropdownIcon = styled.img`
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
  transform: ${(props) => (props.$isOpen ? 'rotate(0deg)' : 'rotate(180deg)')};
`;

const Panel = styled.div`
  position: fixed;
  transform: translateX(-50%);
  width: ${PANEL_WIDTH}px;
  max-height: min(640px, calc(100vh - 16px));
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(24, 23, 24, 0.14);
  z-index: 99999;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  animation: panelFadeIn 0.15s ease-out;

  @keyframes panelFadeIn {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
`;
